// The printed sheet: the same music the screen shows, cut into blocks a page
// break may fall BETWEEN.
//
// Both sheets already know how to draw a slice of themselves — `ChartGrid`
// takes a `rowWindow`, `ScoreView` a `systemWindow` — because page view needs
// exactly that. Print reuses those windows at their finest grain: one block
// per chart row, one per engraved system, each `break-inside: avoid`. The
// browser then packs blocks onto pages itself, which is why nothing here
// measures paper: page size, orientation, margins and the reader's own
// scale-to-fit stay the printer's business, and a page break can never land
// inside a system or halfway through a row of bars.
//
// The masthead opens the sheet as its first block, so page 1 carries title
// and composer and every later page opens with music — the same rule the
// screen's page view follows.

import type { CSSProperties, ReactNode } from 'react';

/** The paper's content width in CSS px, for the sheets that must choose a
 * layout for it (the chart grid picks bars-per-line by width). A4 portrait
 * at the print block's 16mm margins is 794 − 121 = 673px; US Letter is 695.
 * Take the NARROWER: a layout that fits A4 fits Letter with a little more
 * margin, where the reverse crushes the tightest bar on the page. */
export const PRINT_CONTENT_WIDTH = 673;

export function PrintSheet({
  header,
  count,
  render,
  className,
  style,
}: {
  /** The masthead (ChartHeader). Prints on page 1 only, by flow. */
  header?: ReactNode;
  /** Blocks to draw: chart rows, or engraved systems. */
  count: number;
  /** One block: the window is [i, i+1) in the sheet's own units. */
  render: (window: readonly [number, number], index: number) => ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const blocks = Math.max(0, Math.floor(count));
  return (
    <div
      className={className ? `print-sheet ${className}` : 'print-sheet'}
      data-testid="print-sheet"
      // A second copy of the sheet is in the tree while printing; the screen
      // one is what a reader and a screen reader are looking at.
      aria-hidden="true"
      style={style}
    >
      {header && <div className="print-sheet-header">{header}</div>}
      {Array.from({ length: blocks }, (_, i) => (
        <div className="print-block" key={i}>
          {render([i, i + 1] as const, i)}
        </div>
      ))}
    </div>
  );
}
